import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { Controller, useForm } from 'react-hook-form';
import { formatApiDate, parseApiDate } from '../../../Shared/Helpers/dateHelpers';
import {
  getAllowedVatTypesForBatchInvoice,
  getBatchInvoiceVatTypeLabel,
  InvoiceScheme,
  normalizeInvoiceScheme,
  VatRateType,
} from '../../Service/Helpers/batchInvoiceVatTypes';
import type { BatchInvoiceConfig } from '../../Types/batchInvoiceConfigTypes';
import type { BatchInvoiceConfigModalProps } from './BatchInvoiceConfigModal.types';

const BatchInvoiceConfigModal: React.FC<BatchInvoiceConfigModalProps> = ({
  open,
  onClose,
  onConfirm,
  invoiceScheme,
  isLoading,
  itemDescriptionPrefill,
}) => {
  const normalizedScheme = normalizeInvoiceScheme(invoiceScheme);
  const showVatSelector =
    invoiceScheme != null && normalizedScheme === InvoiceScheme.Standard;
  const allowedVatTypes = useMemo(
    () => getAllowedVatTypesForBatchInvoice(invoiceScheme),
    [invoiceScheme]
  );
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<BatchInvoiceConfig>({
    defaultValues: {
      vatType: showVatSelector ? VatRateType.Vat23 : undefined,
      itemDescriptionOverride: itemDescriptionPrefill ?? '',
      invoiceDate: formatApiDate(dayjs()),
    },
  });

  const submitForm = (values: BatchInvoiceConfig): void => {
    onConfirm({
      vatType: showVatSelector ? values.vatType : undefined,
      itemDescriptionOverride: values.itemDescriptionOverride || undefined,
      invoiceDate: values.invoiceDate,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <Box
        component="form"
        onSubmit={(event) => {
          // Dialog is a React-tree descendant of EditBooking's outer form (MUI Dialog
          // portals the DOM but preserves React-tree ancestry), so without this the
          // submit bubbles up and also triggers the booking edit form's own submit.
          event.stopPropagation();
          return handleSubmit(submitForm)(event);
        }}
      >
        <DialogTitle>Konfiguracja faktur wsadowych</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {showVatSelector && (
              <Controller
                name="vatType"
                control={control}
                rules={{ required: 'Stawka VAT jest wymagana' }}
                render={({ field }) => (
                  <FormControl fullWidth error={Boolean(errors.vatType)}>
                    <InputLabel id="batch-invoice-vat-type-label">Stawka VAT</InputLabel>
                    <Select
                      {...field}
                      labelId="batch-invoice-vat-type-label"
                      label="Stawka VAT"
                    >
                      {allowedVatTypes.map((vatType) => (
                        <MenuItem key={vatType} value={vatType}>
                          {getBatchInvoiceVatTypeLabel(vatType)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            )}

            <Controller
              name="itemDescriptionOverride"
              control={control}
              rules={{
                maxLength: {
                  value: 500,
                  message: 'Opis może mieć maksymalnie 500 znaków',
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Opis pozycji"
                  multiline
                  minRows={3}
                  error={Boolean(errors.itemDescriptionOverride)}
                  helperText={errors.itemDescriptionOverride?.message}
                  fullWidth
                />
              )}
            />

            <Controller
              name="invoiceDate"
              control={control}
              rules={{ required: 'Data wystawienia jest wymagana' }}
              render={({ field }) => (
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pl">
                  <DatePicker
                    label="Data wystawienia"
                    value={parseApiDate(field.value)}
                    onChange={(newValue) => field.onChange(formatApiDate(newValue))}
                    slotProps={{
                      field: { clearable: false },
                      textField: {
                        fullWidth: true,
                        error: Boolean(errors.invoiceDate),
                        helperText: errors.invoiceDate?.message,
                      },
                    }}
                  />
                </LocalizationProvider>
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Anuluj</Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            Potwierdź
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default BatchInvoiceConfigModal;
